// src/app/pages/admin/CarguesMasivosWrapper.tsx
import * as XLSX from 'xlsx';
import { useEffect, useRef, useState } from 'react';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {Box, DialogActions, DialogContentText, DialogTitle } from '@mui/material';
import { empresasCargue, candidatos as candidatosCargue, vacantesCargue } from '../../util/listas';
import parameters from '../../util/parameters';
import parameterSingleton from '../../services/Parameters.service';
import { useCargueCandidatos } from './useCargueCandidatos';
import { getGremios, getLocalidades, importEmpresasMasivo, validateEmpresasMasivo, postCargueMasivoVacantes } from '../../modules/apps/user-management/users-list/core/_requests';
import { Localidad } from './EventosEmpleabilidad.helpers';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Fila 1: encabezado, fila 2: obligatoriedad+tipo, fila 3: regla de validación, fila 4: primer dato.
// (mismo formato usado por la plantilla de Candidatos, ver useCargueCandidatos.ts)

type TemplateKey = 'candidatos' | 'empresas' | 'vacantes';

interface TemplateCardModel {
	key: TemplateKey;
	title: string;
	subtitle: string;
	icon: string;
}

const templateHeaders: Record<TemplateKey, string[]> = {
	candidatos: candidatosCargue.Headers,
	empresas: empresasCargue.Headers,
	vacantes: vacantesCargue.Headers 
};

interface UploadCardModel {
	key: string;
	title: string;
	description: string;
	icon: string;
}

const templateCards: TemplateCardModel[] = [
	{
		key: 'candidatos',
		title: 'Plantilla de Candidatos',
		subtitle: 'Formato oficial para perfiles.',
		icon: 'bi-person-check'
	},
	{
		key: 'empresas',
		title: 'Plantilla de Empresas',
		subtitle: 'Formato oficial para perfiles.',
		icon: 'bi-buildings'
	},
	{
		key: 'vacantes',
		title: 'Plantilla de Vacantes',
		subtitle: 'Formato oficial para vacantes.',
		icon: 'bi-send'
	}
];

const uploadCards: UploadCardModel[] = [
	{
		key: 'candidatos',
		title: 'Carga Masiva de Candidatos',
		description: 'Registrar nuevos perfiles de forma ágil desde una plantilla excel.',
		icon: 'bi-person-check-fill'
	},
	{
		key: 'empresas',
		title: 'Carga Masiva de Empresas',
		description: 'Alta masiva de nuevas empresas y sus NITs en el sistema.',
		icon: 'bi-buildings-fill'
	},
	{
		key: 'vacantes',
		title: 'Carga Masiva de Vacantes',
		description: 'Importar múltiples ofertas laborales y sus requisitos técnicos.',
		icon: 'bi-send-fill'
	}
];

let observacionesTotales: string[] = [];

const CarguesMasivosPage = () => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [selectedUpload, setSelectedUpload] = useState<string | null>(null);
	const [keySelect, setKeySelect] = useState<string | null>(null);
	const [uploadedData, setUploadedData] = useState<any[] | null>(null);
	const [nombreArchivoCargado, setNombreArchivoCargado] = useState<string>('');
	// Con archivos grandes, armar el payload (resolverFila por cada fila) y esperar la respuesta del
	// cargue puede tardar; sin este estado el usuario ve la pantalla "congelada" sin ninguna señal de
	// que algo está corriendo.
	const [procesandoCarga, setProcesandoCarga] = useState(false);
	// Mismo problema pero en la lectura/parseo del archivo subido (XLSX.read + validación fila por
	// fila), ANTES de llegar al botón "Procesar Carga" — con un archivo de varios miles de filas esa
	// lectura también puede congelar la pantalla sin ningún indicador.
	const [procesandoArchivo, setProcesandoArchivo] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const handleCloseModal = () => { setOpenModal(false); };
	const [tituloModal, setTituloModal] = useState("");
	const [loading, setLoading] = useState(false);
	const [botonAceptarModal, setBotonAceptarModal] = useState(false);
	const [funcionAceptar, setFuncionAceptar] = useState<(() => void) | null>(null);
	type TipoDialogo = 'error' | 'success' | 'warning' | 'info' | 'question';
	const [tipoDialogo, setTipoDialogo] = useState<TipoDialogo>('info');
	const [mensajeError, setMensajeError] = useState("");
	const [tiposDocumentoEmpresa, setTiposDocumentoEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [naturalezaEmpresa, setNaturalezaEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [tipoEmpresa, setTipoEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [tamanoEmpresa, setTamanoEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [zonas, setZonas] = useState<{ value: string; code: string }[]>([]);	
	const [interesEmpresa, setInteresEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [viaPrincipalEmpresa, setViaPrincipalEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [viaPrincipalPrimeraLetraEmpresa, setViaPrincipalPrimeraLetraEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [viaPrincipalBISLetraEmpresa, setViaPrincipalBISLetraEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [viaPrincipalCuadranteEmpresa, setViaPrincipalCuadranteEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [viaPrincipalDireccionEmpresa, setViaPrincipalDireccionEmpresa] = useState<{ value: string; code: string }[]>([]);
	const [pais, setPais] = useState<{ value: string; code: string }[]>([]);
	const [departamento, setDepartamento] = useState<{ value: string; code: string }[]>([]);	
	const [municipio, setMunicipio] = useState<{ value: string; code: string }[]>([]);
	const [prestadorPreferencia, setPrestadorPreferencia] = useState<{ value: string; code: string }[]>([]);
	const [puntoAtencion, setPuntoAtencion] = useState<{ value: string; code: string }[]>([]);
	const [tiposDocumentoUsuarios, setTiposDocumentoUsuarios] = useState<{ value: string; code: string }[]>([]);
	const [cargoAdministrador, setCargoAdministrador] = useState<{ value: string; code: string }[]>([]);
	const [sexoUsuario, setSexoUsuario] = useState<{ value: string; code: string }[]>([]);
	const [programasCorporativos, setProgramasCorporativos] = useState<{ value: string; code: string }[]>([]);
	const [hayErroresCandidatos, setHayErroresCandidatos] = useState(false);
	const cargueCandidatos = useCargueCandidatos();
	const [localidades, setLocalidades] = useState<Localidad[]>([]);
	const [gremios, setGremios] = useState<{ nombre: string; id: string }[]>([]);

	// Valores finales
	const [empresas, setEmpresas] = useState<any[] | null>(null);
	const [,setVacantes] = useState<any[] | null>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	let filaInicioValidacionEmpresa = 4;
	

	const handleGestionarCarga = (key: string) => {
		setSelectedUpload(key);
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};
	
	const getParameters = async (tipo: string): Promise<{ value: string; code: string }[]> => {
		try {
			const res = await parameterSingleton.fetchData(tipo);
			const values = (res ?? []).map((item: any) => ({
				value: item.value,
				code: item.code,
				label: item.label,
				active: item.active,
			}));
			return values;
		} catch (err) {
			console.error("Error trayendo parametros:", err);
			return [];
		}
	};

	useEffect(() => {
		const cargarTiposDocumento = async () => {
			setTiposDocumentoEmpresa(await getParameters(parameters.TIPODOCUMENTOEMPRESA));
		};

		const cargarNaturalezaEmpresa = async () => {
			setNaturalezaEmpresa(await getParameters(parameters.NATURALEZAEMPRESA));
		};

		const cargarTipoEmpresa = async () => {
			setTipoEmpresa(await getParameters(parameters.TIPOEMPRESA));
		};

		const cargarTamanoEmpresa = async () => {
			setTamanoEmpresa(await getParameters(parameters.TAMANOEMPRESA));
		};

		const cargarInteresEmpresa = async () => { setInteresEmpresa(await getParameters(parameters.INTERESESEMPRESA)); };
		
		const cargarZonas = async () => { setZonas(await getParameters(parameters.ZONA)); };

		const cargarViaPrincipalEmpresa = async () => {
			setViaPrincipalEmpresa(await getParameters(parameters.VIAPRINCIPALEMPRESA));
		};

		const cargarViaPrincipalPrimeraLetraEmpresa = async () => {
			setViaPrincipalPrimeraLetraEmpresa(await getParameters(parameters.VIAPRINCIPALPRIMERALETRA));
		};

		const cargarViaPrincipalBISLetraEmpresa = async () => {
			setViaPrincipalBISLetraEmpresa(await getParameters(parameters.VIAPRINCIPALBIS));
		};

		const cargarViaPrincipalCuadranteEmpresa = async () => {
			setViaPrincipalCuadranteEmpresa(await getParameters(parameters.VIAPRINCIPALCUADRANTE));
		};

		const cargarViaPrincipalDireccionEmpresa = async () => {
			setViaPrincipalDireccionEmpresa(await getParameters(parameters.VIAPRINCIPALDIRECCION));
		};
				
		const cargarPais = async () => {
			setPais(await getParameters(parameters.PAIS));
		};

		const cargarDepartamento = async () => {
			setDepartamento(await getParameters(parameters.DEPARTAMENTO));
		};

		const cargarMunicipio = async () => {
			setMunicipio(await getParameters(parameters.MUNICIPIO));
		};

		const cargarPrestadorPreferencia = async () => {
			setPrestadorPreferencia(await getParameters(parameters.PRESTADORPREFERENCIA));
		};

		const cargarPuntoAtencion = async () => {
			setPuntoAtencion(await getParameters(parameters.PUNTOATENCION));
		};

		const cargarTiposDocumentoUsuarios = async () => {
			setTiposDocumentoUsuarios(await getParameters(parameters.TIPODOCUMENTO));
		};
		const cargarCargoAdministrador = async () => {
			setCargoAdministrador(await getParameters(parameters.CARGOADMINISTRADOR));
		};

		const cargarSexoUsuario = async () => {
			setSexoUsuario(await getParameters(parameters.SEXO));
		};

		const cargarProgramasCorporativos = async () => {
			setProgramasCorporativos(await getParameters(parameters.PROGRAMASCORPORATIVOS));
		};

		const cargarLocalidades = async () => {
			let request = await getLocalidades();
			let res = request?.data;
			setLocalidades(res);
		}

		const cargarGremios = async () => {
			let request = await getGremios();
			let res = request?.data;
			setGremios(res);
		}

		cargarTiposDocumento();
		cargarNaturalezaEmpresa();
		cargarTipoEmpresa();
		cargarTamanoEmpresa();
		cargarInteresEmpresa();
		cargarZonas();
		cargarViaPrincipalEmpresa();
		cargarViaPrincipalPrimeraLetraEmpresa();
		cargarViaPrincipalBISLetraEmpresa();
		cargarViaPrincipalCuadranteEmpresa();
		cargarViaPrincipalDireccionEmpresa();
		cargarPais();
		cargarDepartamento();
		cargarMunicipio();
		cargarPrestadorPreferencia();
		cargarPuntoAtencion();
		cargarTiposDocumentoUsuarios();
		cargarCargoAdministrador();
		cargarSexoUsuario();
		cargarProgramasCorporativos();
		cargarLocalidades();
		cargarGremios();
	}, []);

	const getListaValoresParametros = (tipo: string): { value: string; code: string }[] => {
		const listasPorTipo: Record<string, { value: string; code: string }[]> = {
			[parameters.TIPODOCUMENTOEMPRESA]: tiposDocumentoEmpresa,
			[parameters.NATURALEZAEMPRESA]: naturalezaEmpresa,
			[parameters.TIPOEMPRESA]: tipoEmpresa,
			[parameters.TAMANOEMPRESA]: tamanoEmpresa,
			[parameters.INTERESESEMPRESA]: interesEmpresa,
			[parameters.ZONA]: zonas,
			[parameters.VIAPRINCIPALEMPRESA]: viaPrincipalEmpresa,
			[parameters.VIAPRINCIPALPRIMERALETRA]: viaPrincipalPrimeraLetraEmpresa,
			[parameters.VIAPRINCIPALBIS]: viaPrincipalBISLetraEmpresa,
			[parameters.VIAPRINCIPALCUADRANTE]: viaPrincipalCuadranteEmpresa,
			[parameters.VIAPRINCIPALDIRECCION]: viaPrincipalDireccionEmpresa,
			[parameters.PAIS]: pais,
			[parameters.DEPARTAMENTO]: departamento,
			[parameters.MUNICIPIO]: municipio,
			[parameters.PRESTADORPREFERENCIA]: prestadorPreferencia,
			[parameters.PUNTOATENCION]: puntoAtencion,
			[parameters.TIPODOCUMENTO]: tiposDocumentoUsuarios,
			[parameters.CARGOADMINISTRADOR]: cargoAdministrador,
			[parameters.SEXO]: sexoUsuario,
			[parameters.PROGRAMASCORPORATIVOS]: programasCorporativos,
		};
		return listasPorTipo[tipo] ?? [];
	}

	const getCodeParametro = (tipo: string, valor: string, campo: string = "value"): any => {
		let listaParametros = getListaValoresParametros(tipo) || [];
		const valorTexto = String(valor).trim().toLowerCase();
		let parametroEncontrado;
		
		parametroEncontrado = listaParametros.find((x: any) => 
			String(x[campo]).trim().toLowerCase() == valorTexto
		);
		return parametroEncontrado ? parametroEncontrado.code : "";
	}

	const getIdLocalidad = (valor: string): any => {
		let listaLocalidades = localidades || [];
		const valorTexto = String(valor).trim().toLowerCase();
		const localidadEncontrada = listaLocalidades.find((x: any) => 
			String(x.desc_localidad).trim().toLowerCase() == valorTexto
		);
		return localidadEncontrada ? localidadEncontrada.id_localidad : "";
	}

	const getIdGremio = (valor: string): any => {
		let listaGremios = gremios || [];
		const valorTexto = String(valor).trim().toLowerCase();
		const gremioEncontrado = listaGremios.find((x: any) => 
			String(x.nombre).trim().toLowerCase() == valorTexto
		);
		return gremioEncontrado ? gremioEncontrado.id : null;
	}

	const mostrarDialogo = (titulo: string, mensaje: string, tipo: TipoDialogo = 'info') => {
		setTituloModal(titulo);
		setMensajeError(mensaje);
		setTipoDialogo(tipo);
		setBotonAceptarModal(false);
		setOpenModal(true);		
	}

	const mostrarDialogoConfirmar = (titulo: string, mensaje: string, tipo: TipoDialogo = 'info', callback?: () => void) => {
		setTituloModal(titulo);
		setMensajeError(mensaje);
		setTipoDialogo(tipo);
		setBotonAceptarModal(true);
		setFuncionAceptar(() => callback || null);
		setOpenModal(true);
	};

	const handleAceptarConfirmar = () => {
		if (funcionAceptar) {
			funcionAceptar();
		}
		setOpenModal(false);
	};

	const obtenerIconoDialogo = () => {
		switch (tipoDialogo) {
			case 'error':
				return <ErrorIcon sx={{ color: '#dc3545', fontSize: 40 }} />;
			case 'success':
				return <CheckCircleIcon sx={{ color: '#198754', fontSize: 40 }} />;
			case 'warning':
				return <WarningIcon sx={{ color: '#ffc107', fontSize: 40 }} />;
			case 'info':
				return <InfoIcon sx={{ color: '#0d6efd', fontSize: 40 }} />;
			 case 'question':
            	return <HelpOutlineIcon sx={{ color: '#6f42c1', fontSize: 40 }} />;
			default:
				return <InfoIcon sx={{ color: '#0d6efd', fontSize: 40 }} />;
		}
	};

	const tieneError = uploadedData?.some((row: any) =>
		String(row['Estado'] ?? '').toUpperCase().includes('ERROR')
	);

	// Quita las etiquetas HTML (<b>, <br>, <span>...) que trae la columna "Observación" para dejar
	// texto plano legible en el .txt.
	const limpiarObservacionHtml = (html: any): string =>
		String(html ?? '')
			.replace(/<br\s*\/?>/gi, '\n')
			.replace(/<\/?[^>]+(>|$)/g, '')
			.trim();

	// Genera y descarga un .txt con las filas en Estado ERROR de la previsualización actual
	// (aplica por igual a Empresas/Vacantes/Candidatos, ya que las 3 comparten la misma forma
	// {N° Fila, Estado, Observación} en `uploadedData`).
	const descargarErroresTxt = (tipo: any ="Errores") => {
		
		if (!uploadedData) return;
		//const filasConError = uploadedData.filter((row: any) => String(row['Estado'] ?? '').includes('ERROR'));
		const filasConError = uploadedData.filter((row: any) =>
			['ERROR', 'EXISTE', 'CREADA'].some((estado) =>
				String(row['Estado'] ?? '').toUpperCase().includes(estado)
			)
		);

		if (filasConError.length === 0) return;

		const lineas = filasConError.map((row: any) => {
			const fila = row['N° Fila'] ?? '';
			return `Fila ${fila}:\n${limpiarObservacionHtml(row['Observación'])}\n`;
		});
		const contenido = `${tipo} de la carga de ${keySelect} (${filasConError.length} fila(s))\n${'='.repeat(60)}\n\n${lineas.join('\n')}`;

		const blob = new Blob([contenido], { type: 'text/plain;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${tipo}_${keySelect}_${new Date().toISOString().slice(0, 10)}.txt`;
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);
	};

	// Genera y descarga un .csv (UTF-8 con BOM, separado por ';') SOLO para la carga de Empresas:
	// contiene la información cargada originalmente por el usuario y, al final, las columnas
	// "Estado" y "Error" con la descripción de cada error por fila.
	const descargarErroresCsv = (tipo: any = "Errores") => {
		if (!uploadedData || !empresas || empresas.length === 0) return;

		const filasConError = uploadedData.filter((row: any) =>
			['ERROR', 'EXISTE', 'CREADA'].some((estado) =>
				String(row['Estado'] ?? '').toUpperCase().includes(estado)
			)
		);
		if (filasConError.length === 0) return;

		const SEP = ';';
		const escapar = (valor: any): string => {
			const texto = String(valor ?? '');
			return /[";\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
		};

		const encabezados = Object.keys(empresas[0]);
		const lineas: string[] = [
			[...encabezados, 'N° Fila', 'Estado', 'Error'].map(escapar).join(SEP),
		];

		filasConError.forEach((row: any) => {
			const numFila = Number(row['N° Fila']);
			const original = empresas[numFila - filaInicioValidacionEmpresa] ?? {};
			const estado = limpiarObservacionHtml(row['Estado']);
			const error = limpiarObservacionHtml(row['Observación']).replace(/\n/g, ' | ');
			lineas.push(
				[...encabezados.map((h) => original[h]), numFila, estado, error].map(escapar).join(SEP)
			);
		});

		const blob = new Blob(['﻿' + lineas.join('\r\n')], { type: 'text/csv;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${tipo}_${keySelect}_${new Date().toISOString().slice(0, 10)}.csv`;
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);
	};

	// Genera la plantilla .xlsx (Empresas / Vacantes, sin dropdowns nativos por ahora).
	const handleDescargarPlantilla = (key: TemplateKey) => {
		if (key === 'candidatos') {
			cargueCandidatos.descargarPlantilla();
			return;
		}

		if (key == 'empresas') {
			descargarPlantillaEmpresas();
			return;
		}

		descargarPlantillaVacantes();
	};

	const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, key: string | null) => {

		const file = e.target.files?.[0];
		if (!file) return;

		// Se rechaza el archivo por formato ANTES de intentar leer/procesar su contenido,
		// informando los formatos aceptados (Candidatos solo admite Excel; Empresas y Vacantes también CSV UTF-8).
		const formatosAceptados = key === 'candidatos' ? ['.xlsx', '.xls'] : ['.xlsx', '.xls', '.csv'];
		const nombreLower = file.name.toLowerCase();
		if (!formatosAceptados.some((ext) => nombreLower.endsWith(ext))) {
			const detalleCsv = formatosAceptados.includes('.csv') ? ' (los .csv deben estar codificados en UTF-8)' : '';
			mostrarDialogo('Archivo rechazado', `El archivo "${file.name}" no tiene un formato válido. Formatos aceptados: ${formatosAceptados.join(', ')}${detalleCsv}.`);
			if (fileInputRef.current) fileInputRef.current.value = '';
			return;
		}

		setNombreArchivoCargado(file.name);

		setSelectedFile(file);
		setProcesandoArchivo(true);
		setUploadedData([]);

		const reader = new FileReader();
		reader.onload = (evt) => {
		  try {
			const buffer = evt.target?.result;
			if (!buffer) return;

			let workbook: XLSX.WorkBook;
			if (file.name.toLowerCase().endsWith('.csv')) {
				const firma = new Uint8Array(buffer as ArrayBuffer, 0, 2);
				if (firma[0] === 0x50 && firma[1] === 0x4b) {
					mostrarDialogo('Error al cargar el archivo', 'El archivo tiene extensión .csv pero su contenido es un archivo Excel. Guárdelo como CSV desde Excel o cárguelo como .xlsx.');
					return;
				}
				// Los CSV se decodifican explícitamente como UTF-8 (con o sin BOM) y se rechazan otras codificaciones.
				// XLSX.read sobre el buffer binario altera tildes/ñ en UTF-8 sin BOM y los encabezados no coinciden.
				let texto: string;
				try {
					texto = new TextDecoder('utf-8', { fatal: true }).decode(buffer as ArrayBuffer);
				} catch {
					mostrarDialogo('Archivo rechazado', `El archivo "${file.name}" no está codificado en UTF-8. Guárdelo como "CSV UTF-8 (delimitado por comas)" e inténtelo nuevamente.`);
					return;
				}
				const primeraLinea = texto.split(/\r?\n/)[0] || '';
				const FS = (primeraLinea.match(/;/g) || []).length >= (primeraLinea.match(/,/g) || []).length ? ';' : ',';
				workbook = XLSX.read(texto, { type: 'string', FS });
			} else {
				workbook = XLSX.read(buffer, { type: 'array' });
			}

			// Buscamos la hoja llamada "plantilla"
			let plantilla = "";
			switch (key) {
				case 'empresas':
					plantilla = 'plantilla'
					break;
				case 'candidatos':
					plantilla = 'candidatos'
					break;
				case 'vacantes':
					plantilla = 'plantilla'
					break;
				default:
					plantilla = 'plantilla';
			}

			let sheetName = workbook.SheetNames.find(
				(name) => name.trim().toLowerCase() === plantilla.toLowerCase()
			);

			if (!sheetName && workbook.SheetNames.length > 0) {
				sheetName = workbook.SheetNames[0];
			}

			if (!sheetName) {
				mostrarDialogo('Error al cargar el archivo', `No se encontró ninguna hoja válida en el archivo.`);
				return;
			}

			const worksheet = workbook.Sheets[sheetName];

			if (worksheet) {
				const range = { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };
				Object.keys(worksheet).forEach((cellKey) => {
					if (cellKey.startsWith('!')) return;
					const cell = XLSX.utils.decode_cell(cellKey);
					if (cell.r > range.e.r) range.e.r = cell.r;
					if (cell.c > range.e.c) range.e.c = cell.c;
				});
				worksheet['!ref'] = XLSX.utils.encode_range(range);
			}

			const rawDataRaw = XLSX.utils.sheet_to_json(worksheet, { 
				header: 1, 
				defval: "", 
				blankrows: false 
			}) as any[][];

			const filasValidas = rawDataRaw.filter(row => 
				row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== "")
			);

			if (!filasValidas || filasValidas.length <= 1) {
				mostrarDialogo("Error al cargar el archivo", "El archivo está vacío o solo contiene encabezados.");
				return;
			}

			const headers = filasValidas[0].map(h => 
				String(h).replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim()
			);

			const rawDataAll = filasValidas.slice(1).map((row) => {
				const rowObj: Record<string, any> = {};
				headers.forEach((header, index) => {
					rowObj[header] = row[index] !== undefined ? row[index] : "";
				});
				return rowObj;
			});

			let data = [];
			switch (key) {
				case 'empresas': { 
					const faltantesEmpresa = validarEncabezadoEmpresa(headers);
					if (faltantesEmpresa.length > 0) {
						mostrarDialogo("Validación encabezados", `Encabezados faltantes en la plantilla de Empresas:\n- ${faltantesEmpresa.join('\n- ')}`);
						return;
					}
					// filaInicioValidacionEmpresa = 4;
					const rawDataEmpresa = rawDataAll.slice(filaInicioValidacionEmpresa - 2);
					// Para empezar en la fila 4, ignoramos las filas anteriores.
					data = validarEmpresaCarga(rawDataEmpresa, filaInicioValidacionEmpresa);
					setEmpresas(rawDataEmpresa);
					break; 
				}
				case 'candidatos': {
					const resultadoCandidatos = cargueCandidatos.procesarArchivoSubido(rawDataAll);
					if (resultadoCandidatos.faltantes.length > 0) {
						mostrarDialogo("Validación encabezados", `Encabezados faltantes en la plantilla de Candidatos:\n- ${resultadoCandidatos.faltantes.join('\n- ')}`);
						return;
					}
					if (resultadoCandidatos.excedeMaximo) {
						mostrarDialogo("Archivo rechazado", `El archivo contiene ${resultadoCandidatos.totalRegistros} registros y supera el máximo permitido de 10.000 por carga. Divida el archivo en lotes más pequeños e intente nuevamente.`);
						return;
					}
					data = resultadoCandidatos.data;
					setHayErroresCandidatos(resultadoCandidatos.hayErrores);
					break;
				}
				case 'vacantes': { 
					const faltantesVacante = validarEncabezadoVacante(headers);
					if (faltantesVacante.length > 0) {
						mostrarDialogo("Validación encabezados", `Encabezados faltantes en la plantilla de Vacantes:\n- ${faltantesVacante.join('\n- ')}`);
						return;
					}

					// La plantilla oficial trae fila 2 (tipo, "[Obligatorio] ...") y fila 3 (regla): los datos inician en la fila 4.
					// Las plantillas antiguas (solo encabezado) inician en la fila 2.
					const primeraCelda = String(filasValidas[1]?.[0] ?? '').trim();
					const tieneFilasGuia = primeraCelda.startsWith('[Obligatorio]') || primeraCelda.startsWith('[Opcional]');
					const filaInicioVacante = tieneFilasGuia ? 4 : 2;
					const dataProcesada = validarVacanteCarga(rawDataAll.slice(filaInicioVacante - 2), filaInicioVacante);
					data = dataProcesada;
					break; 
				}
			}

			setUploadedData(data as any[]);
			setKeySelect(key);

			// Reseteamos el input de archivo
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		  } catch (error) {
			console.error('Error leyendo el archivo de cargue:', error);
			mostrarDialogo('Error al cargar el archivo', 'No se pudo leer el archivo. Verifique que sea un archivo Excel o CSV válido.');
		  } finally {
			setProcesandoArchivo(false);
		  }
		};
		reader.onerror = () => {
			setProcesandoArchivo(false);
			mostrarDialogo('Error al cargar el archivo', 'No se pudo leer el archivo seleccionado.');
		};

		reader.readAsArrayBuffer(file);
	};

	const validarEncabezadoEmpresa = (headers: string[]): string[] => {
		const requiredHeaders = templateHeaders.empresas;
		let noExistentes = requiredHeaders.filter(h => !headers.includes(h));
		return noExistentes;
	};

	const validarEncabezadoVacante = (headers: string[]): string[] => {
		const requiredHeaders = vacantesCargue.CamposObligatorios;		
		const headersLimpios = headers.map(h => (h ? h.toString().trim() : ''));
		return requiredHeaders.filter(req => !headersLimpios.includes(req.trim()));
	};

	
	const getValorUnico = (row: any, campo1: string, campo2?: string) => {
		const v1 = row[campo1]?.toString().trim() || "";
		const v2 = (campo2 && row[campo2]?.toString().trim()) || "";
		if (!v1 || (campo2 && !v2)) return null;
		return {
			clave: `${v1}_${v2}`.toLowerCase(),
			etiqueta: campo2 ? `${campo1} y ${campo2}` : campo1,
			valor: campo2 ? `${v1} - ${v2}` : v1,
		};
	};
	/* Acá se hacen las validaciones para la empresa */
	const validarEmpresaCarga = (rawData: any, filaInicio: number) => {
		observacionesTotales = [];		

		const repeticiones: Record<string, Map<string, number>> = {};
		empresasCargue.CamposUnicos.forEach(({ campo1, campo2 }) => {
			const conteo = new Map<string, number>();
			rawData.forEach((row: any) => {
				const info = getValorUnico(row, campo1, campo2);
				if (info) conteo.set(info.clave, (conteo.get(info.clave) || 0) + 1);
			});
			repeticiones[`${campo1}_${campo2}`] = conteo;
		});
		
		// ************** Inicia el proceso de pre-validación de datos de la empresa ***************/
		let razon_social = '';
		let datos = rawData.map((row: any, index: number) => {
			
			let observaciones: string[] = [];
			razon_social = row["Razón social"] || '';
			
			// ********** Validar campos obligatorios *************
			empresasCargue.CamposObligatorios.forEach((campo: string) => {
				const valor = row[campo];
				if (valor == null || String(valor).trim() === "") {
					observaciones.push(`<b>${campo}</b> es obligatorio`);
				}
			});

			// ********** Validar cantidad de caracteres **************
			empresasCargue.LimitesCaracteres?.forEach(({ campo, max, min }) => {
				const valor = row[campo];
				// Solo se valida longitud si el campo contiene algún valor
				if (valor != null && String(valor).trim() !== "") {
					const texto = String(valor).trim();
					if (max && texto.length > max) {
						observaciones.push(`<b>${campo}</b>: Valor supera el máximo de ${max} caracteres (tiene ${texto.length})`);
					}
					if (min && texto.length < min) {
						observaciones.push(`<b>${campo}</b>: Valor debe tener al menos ${min} caracteres (tiene ${texto.length})`);
					}
				}
			});

			// ********** Validar valores repetidos en el cargue **************
			empresasCargue.CamposUnicos.forEach(({ campo1, campo2 }) => {
				const info = getValorUnico(row, campo1, campo2);
				if (info && (repeticiones[`${campo1}_${campo2}`]?.get(info.clave) || 0) > 1) {
					observaciones.push(`<b>${info.etiqueta}</b>: Valor '${info.valor}' está duplicado en el archivo`);
				}
			});

			// ********* Validar campos de tipo lista *************
			empresasCargue.camposEnParametros?.forEach(({ campo, tipo, campoParam }) => {
				const valor = row[campo];
				if (valor != null && String(valor).trim() !== "") {
					let listaParametros = getListaValoresParametros(tipo) || [];
					const valorTexto = String(valor).trim().toLowerCase();
					const existe = listaParametros.some((x: any) => String(x[campoParam || 'value']).trim().toLowerCase() == valorTexto);

					if (!existe) {
						observaciones.push(`<b>${campo}</b>: El valor '${valor}' no es válido`);
					}
				}
			});

			//***************** Validación localidades ****************/
			if (row["Departamento (Información de contacto)"]?.trim().toUpperCase() == "BOGOTÁ, D.C.") {

				if (row["Localidad (Información de contacto)"] == null || row["Localidad (Información de contacto)"] == "") {
					observaciones.push(`<b>Localidad (Información de contacto)</b>: El valor '${row["Localidad (Información de contacto)"]}' es obligatorio`);
				}
				else {
					const valorLocalidad = row["Localidad (Información de contacto)"]?.trim().toLowerCase();					
					const existe = localidades.some((x: any) => String(x.desc_localidad).trim().toLowerCase() == valorLocalidad);
					if (!existe) {
						observaciones.push(`<b>Localidad (Información de contacto)</b>: El valor '${valorLocalidad}' no es válido`);
					}
				}
			}

			//***************** Validación Gremios Principal ****************/			
			if (row["Gremio Principal"] != null && row["Gremio Principal"] != "") {
				const valorGremio = row["Gremio Principal"]?.trim().toLowerCase();					
				const existe = gremios.some((x: any) => String(x.nombre).trim().toLowerCase() == valorGremio);
				if (!existe) {
					observaciones.push(`<b>Gremio Principal</b>: El valor '${valorGremio}' no es válido`);
				}
			}
			//***************** Validación Gremios Secundario ****************/			
			if (row["Gremio secundario"] != null && row["Gremio secundario"] != "") {
				const valorGremio = row["Gremio secundario"]?.trim().toLowerCase();					
				const existe = gremios.some((x: any) => String(x.nombre).trim().toLowerCase() == valorGremio);
				if (!existe) {
					observaciones.push(`<b>Gremio secundariol</b>: El valor '${valorGremio}' no es válido`);
				}
			}
			
			
			// Se agregan los errores
			observacionesTotales.push(...observaciones);

			return {
				"N° Fila": index + filaInicio,
				"Razón social": razon_social,
				Estado: observaciones.length > 0 ? colocarColores("ERROR") : colocarColores("OK"),
				Observación: observaciones.join("<br>"),
				// ...rest
			};
		});
		
		return datos;
	}

	/* Acá se hacen las validaciones para la vacante */
	const validarVacanteCarga = (rawData: any, filaInicio: number) => {
		
		let datos = rawData.map((row: any, index: number) => {
			const { Campo, 'Tipo de dato': tipoDato, Obligatorio, encontrado, 'Regla de negocio detallada': reglaNegocio, ...rest } = row;
			return {
				'N° Fila': index + filaInicio,
				"Razón social": row.razon_social,
				'Estado': 'OK',
				'Observación': '',
				...rest
			};
		});
		return datos;
	}

	const handleProcesarCarga = () => {

		if (!uploadedData || uploadedData?.length === 0) {
			mostrarDialogo("Hay registros con errores", "Hay temas pendientes de solucionar para continuar con el proceso", "warning");
			return;
		}
		//console.log("Observaciones: ",uploadedData);
		const hayErrores = keySelect === 'candidatos' ? hayErroresCandidatos : observacionesTotales.length > 0;
		if (hayErrores) {
			mostrarDialogo("Hay registros con errores", "Hay temas pendientes de solucionar para continuar con el proceso", "warning");
			return;
		}

		switch (keySelect) {
			case 'empresas':
				procesarEmpresasCarga(empresas);
				break;
			case 'candidatos':
				setProcesandoCarga(true);
				// setTimeout(0) para que React pinte el estado "procesando" ANTES de arrancar el trabajo
				// pesado y síncrono de armar el payload (resolverFila por cada fila) — si se llama directo,
				// el spinner nunca llega a pintarse porque el hilo de JS queda ocupado de inmediato.
				setTimeout(() => {
					cargueCandidatos.procesar(uploadedData, nombreArchivoCargado).then((resultado) => {
						if (resultado.nuevasFilas) setUploadedData(resultado.nuevasFilas);
						const resumen = resultado.resumen;
						const mensajeResumen = resumen
							? `${resultado.mensaje}\n\n` +
								`Resumen del cargue\n` +
								`ID: ${resumen.id}\n` +
								`Fecha de cargue masivo: ${new Date(resumen.fechaCargue).toLocaleString('es-CO')}\n` +
								`Nombre del archivo: ${resumen.nombreArchivo}\n` +
								`Registros procesados: ${resumen.registrosProcesados}\n` +
								`Registros con errores: ${resumen.registrosConErrores}\n` +
								`Total de registros insertados: ${resumen.registrosInsertados}`
							: resultado.mensaje;
						mostrarDialogo(resultado.titulo, mensajeResumen);
					}).catch((error) => {
						// Sin este catch, cualquier excepción antes de que la promesa resuelva (ej. dentro de
						// resolverFila al armar el payload) dejaba la carga colgada sin mostrar nada en
						// pantalla: quedaba como una promesa rechazada sin manejar.
						console.error('Error procesando la carga masiva de candidatos:', error);
						mostrarDialogo('Error al procesar la carga', error?.message || 'Ocurrió un error inesperado procesando la carga masiva de candidatos. Revisa la consola del navegador para más detalle.');
					}).finally(() => {
						setProcesandoCarga(false);
					});
				}, 0);
				break;
			case 'vacantes':
				procesarVacantesCarga(uploadedData);
				break;
			default:
				console.error('Entidad de carga no reconocida:', keySelect);
				alert('Hubo un error al identificar el tipo de carga.');
		}
	};

	const colocarColores = (estado: string) => {
		switch (estado) {
			case "OK":
				return `<span class='text-success'>${estado}</span>`;
			case "EXISTE":
				return `<span class='text-warning'>${estado}</span>`;
			case "ERROR":
				return `<span class='text-danger'>${estado}</span>`;
			case "CREADA":
				return `<span class='text-success'>${estado}</span>`;
			default:
				return `<span>${estado}</span>`;
		}
	}

	const respEmpresas = (data: any) =>{
		let resultsEmpresas = data?.map((row: any, index: number) => {				
			return {
				"N° Fila": index + filaInicioValidacionEmpresa,
				"Razón social": row.razon_social,
				Estado: colocarColores(row.estado),
				Observación: colocarColores(row.observacion || ''),
			};
			
		});
		return resultsEmpresas;
		
	}

	/* Metodos para el envio de datos de la empresa */
	const procesarEmpresasCarga = async (data: any) => {
		setLoading(true);
		setProcesandoArchivo(true);
		let dataMapping = mappingEmpresa(data);
		
		// Validar empresa del lado del servidor
		let r = await validarEmpresas(dataMapping);
		
		let dataValidation = r?.data?.resultsEmpresas;
		
		if(dataValidation?.length > 0){	
			let resultsEmpresas = respEmpresas(dataValidation);		
			setUploadedData(resultsEmpresas);

			let noExitosos = dataValidation.filter((x: any) => !x.exitoso);
			if(noExitosos.length > 0){
				mostrarDialogo("Hay registros con errores", "Verifique los errores en la tabla para continuar con el proceso", "error");
			}
			else{
				// Guardamos empresas una vez validados 
				mostrarDialogoConfirmar("Confirmación de carga", "¿Estás seguro de continuar con el proceso de carga de empresa?", "question",
					async () => await handleGuardarEmpresa(dataMapping)
				);
			}			
		}
		else{
			mostrarDialogo("Proceso exitoso", r?.data?.message, "success");
		}
		setProcesandoArchivo(false);
		setLoading(false);
	};

	const handleGuardarEmpresa = async (dataMapping: any)=>{
		setProcesandoArchivo(true);
		setLoading(true);
		
		let r = await guardarEmpresa(dataMapping);
		let dataValidation = r?.data?.resultsEmpresas;

		if(dataValidation?.length > 0){	
			let resultsEmpresas = respEmpresas(dataValidation);		
			setUploadedData(resultsEmpresas);

			let noExitosos = dataValidation.filter((x: any) => !x.exitoso);
			if(noExitosos.length > 0){
				mostrarDialogo("Hay registros con errores", "Verifique los errores en la tabla para continuar con el proceso", "error");
			}
			else{
				mostrarDialogo("Proceso exitoso", "Se han agregado las empresas correctamente", "success");
			}
			
		}
		else{
			mostrarDialogo("Error", "Ha ocurrido error en el proceso. Vuelva a intentar", "error");
		}

		setProcesandoArchivo(false);
		setLoading(false);
	};

	const mappingEmpresa = (dataEmpresa: any)=>{
		const empresa: any = {};

		const empresas = dataEmpresa.map((data: any) => {
			const empresa: any = {};

			// =========================================================
			// EMPRESA
			// =========================================================

			empresa.razon_social = data["Razón social"];
			empresa.tipo_documento = getCodeParametro(parameters.TIPODOCUMENTOEMPRESA, data["Tipo de documento"]);
			empresa.numero_documento = data["Número de documento"];
			empresa.telefono = data["Teléfono (Información de contacto)"];
			empresa.extension_telefono = data["Extensión del teléfono (Información de contacto)"];

			empresa.naturaleza = getCodeParametro(parameters.NATURALEZAEMPRESA, data["Naturaleza"]);
			empresa.tipo = getCodeParametro(parameters.TIPOEMPRESA, data["Tipo"]);
			empresa.Tamano_empleados = getCodeParametro(parameters.TAMANOEMPRESA, data["Tamaño por número de empleados"]);

			empresa.descricion_employer = data["Descripción de la compañía"];

			

			// Dirección
			empresa.local = getCodeParametro(parameters.VIAPRINCIPALEMPRESA, data["Vía principal (Dirección sede)"]);			
			empresa.id_coordenada =  data["Número 1 (Dirección sede)"];
			empresa.id_coordenada_02 = getCodeParametro(parameters.VIAPRINCIPALPRIMERALETRA, data["Letra 1 (Dirección sede)"]);
			empresa.id_coordenada_03 = getCodeParametro(parameters.VIAPRINCIPALBIS, data["BIS 1 (Dirección sede)"]);
			empresa.id_coordenada_04 = getCodeParametro(parameters.VIAPRINCIPALPRIMERALETRA, data["Letra 2 (Dirección sede)"]);
			empresa.id_coordenada_05 = getCodeParametro(parameters.VIAPRINCIPALCUADRANTE, data["Cardinalidad 1 (Dirección sede)"]);
			empresa.id_coordenada_06 = data["Número 2 (Dirección sede)"];
			empresa.id_coordenada_07 = getCodeParametro(parameters.VIAPRINCIPALPRIMERALETRA, data["Letra 3 (Dirección sede)"]);
			empresa.id_coordenada_08 = data["Número 3 (Dirección sede)"];
			empresa.tipo_complemento = getCodeParametro(parameters.VIAPRINCIPALDIRECCION, data["Tipo de complemento (Dirección sede)"]);

			empresa.codigo_postal = null;

			empresa.direccion_residencia = data["Dirección sede"];

			empresa.pais = getCodeParametro(parameters.PAIS, data["País (Información de contacto)"]);

			empresa.departamento = getCodeParametro(parameters.DEPARTAMENTO, data["Departamento (Información de contacto)"]);

			empresa.municipio = getCodeParametro(parameters.MUNICIPIO, data["Municipio (Información de contacto)"]);

			empresa.localidad = getIdLocalidad(data["Localidad (Información de contacto)"]);

			empresa.prestador_principal = getCodeParametro(parameters.PRESTADORPREFERENCIA, data["Prestador principal (Información de contacto)"]);

			empresa.punta_atencion = getCodeParametro(parameters.PUNTOATENCION, data["Punto de atención (Información de contacto)"]);

			//---------
			empresa.pertence = getCodeParametro(parameters.ZONA, data["Pertenece a zona"]);

			// Dirección de contacto
			empresa.contacto_direccion = data["Dirección notificación"];

			let ContactoViaPrincipal = getCodeParametro(parameters.VIAPRINCIPALEMPRESA, data["Vía principal (Dirección notificación)"]) ?? '';
			let ContactoNumeroPrincipal = data["Número 1 (Dirección notificación)"] ?? '';
			let ContactoLetraPrincipal = getCodeParametro(parameters.VIAPRINCIPALPRIMERALETRA, data["Letra 1 (Dirección notificación)"]) ?? '';
			let ContactoBisPrincipal = getCodeParametro(parameters.VIAPRINCIPALBIS, data["BIS 1 (Dirección notificación)"]) ?? '';
			let ContactoLetraPrincipal2 = getCodeParametro(parameters.VIAPRINCIPALPRIMERALETRA, data["Letra 2 (Dirección notificación)"]) ?? '';
			let ContactoCardinalidadPrincipal = getCodeParametro(parameters.VIAPRINCIPALCUADRANTE, data["Cardinalidad 1 (Dirección notificación)"]) ?? '';
			let ContactoNumeroPrincipal2 = data["Número 2 (Dirección notificación)"] ?? '';
			let ContactoLetraPrincipal3 = getCodeParametro(parameters.VIAPRINCIPALPRIMERALETRA, data["Letra 3 (Dirección notificación)"])?? '';
			let ContactoNumeroPrincipal3 = data["Número 3 (Dirección notificación)"] ?? '';
			let ContactoCardinalidadPrincipal2 = getCodeParametro(parameters.VIAPRINCIPALCUADRANTE, data["Cardinalidad 2 (Dirección notificación)"]) ?? '';
			let ContactoTipoComplementoPrincipal = getCodeParametro(parameters.VIAPRINCIPALDIRECCION, data["Tipo de complemento (Dirección notificación)"]) ?? '';
			let ContactoComplementoPrincipal = data["Complemento (Dirección notificación)"] ?? '';

			empresa.contacto_direccion_coordenadas = `${ContactoViaPrincipal}|${ContactoNumeroPrincipal}|${ContactoLetraPrincipal}|${ContactoBisPrincipal}|${ContactoLetraPrincipal2}|${ContactoCardinalidadPrincipal}|${ContactoNumeroPrincipal2}|${ContactoLetraPrincipal3}|${ContactoNumeroPrincipal3}|${ContactoCardinalidadPrincipal2}|${ContactoTipoComplementoPrincipal}|${ContactoComplementoPrincipal}`;

			// Representante legal
			empresa.repre_nombre1 = data["Primer nombre (Información del representante legal)"];
			empresa.repre_nombre2 = data["Segundo nombre (Información del representante legal)"];
			empresa.repre_apellido1 = data["Primer apellido (Información del representante legal)"];
			empresa.repre_apellido2 = data["Segundo apellido (Información del representante legal)"];
			empresa.repre_tipo_documento = getCodeParametro( parameters.TIPODOCUMENTO, data["Tipo de documento (Información del representante legal)"] );
			empresa.repre_numero_documento = data["Número de documento (Información del representante legal)"];
			empresa.repre_cargo = getCodeParametro( parameters.CARGOADMINISTRADOR, data["Cargo (Información del representante legal)"]);
			empresa.repre_telefono = data["Teléfono (Información del representante legal)"];
			empresa.repre_correo_electronico = data["Correo electrónico (Información del representante legal)"];

			//---------

			empresa.nombre_sede = data["Nombre de la sede"];

			empresa.codigo_ciiu = data["Código CIIU"];

			empresa.programas_corporativos = getCodeParametro(parameters.PROGRAMASCORPORATIVOS, data["Implementa acciones y/o hace parte de programas corporativos"], "label");

			empresa.intereses_empresa = getCodeParametro(parameters.INTERESESEMPRESA, data["Interés de la Empresa"]);; 

			empresa.contacto_correo_electronico = data["Correo electrónico (Información de contacto)"];

			empresa.gremio_principal_id = getIdGremio(data["Gremio Principal"]);

			empresa.gremio_secundario_id = getIdGremio(data["Gremio secundario"]);


			// =========================================================
			// USUARIO
			// =========================================================

			const correoUsuario = data["Correo electrónico (Datos admin de la cuenta plataforma"];

			const firstUser: any = {
				user: {
					email: correoUsuario,
					confirma_email: correoUsuario,
					role: 4,
					active: true,
					acepto_termos: true,
					dados_personales: true,
				},

				complemento: {
					tipo_doc: getCodeParametro(parameters.TIPODOCUMENTO,data["Tipo de documento (Datos admin de la cuenta plataforma)"]),

					numero_doc:
						data[
							"Número de documento (Datos admin de la cuenta plataforma)"
						],

					primer_nome:
						data[
							"Primer nombre (Datos admin de la cuenta plataforma)"
						],

					segundo_nome:
						data[
							"Segundo nombre (Datos admin de la cuenta plataforma)"
						],

					primer_apelido:
						data[
							"Primer apellido (Datos admin de la cuenta plataforma)"
						],

					segundo_apelido:
						data[
							"Segundo apellido (Datos admin de la cuenta plataforma)"
						],

					celular:
						data[
							"Teléfono (Datos admin de la cuenta plataforma)"
						],

					correo: correoUsuario,

					cargo: getCodeParametro(parameters.CARGOADMINISTRADOR, data["Cargo (Datos admin de la cuenta plataforma)"]),
				},
			};

			return {
				firstCompany: {
					company: empresa,

					complemento: [
						{
							tipo_complemento:
								data["Tipo de complemento (Dirección sede)"],

							complemento:
								data["Complemento (Dirección sede)"],
						},
					],
				},

				firstUser,
			};
		});
		console.log("Empresa: ", empresas);
		return empresas;		
	}

	const guardarEmpresa = async(empresa: any)=>{		
		return await importEmpresasMasivo(empresa);
	}

	const validarEmpresas = async(empresa: any)=>{		
		return await validateEmpresasMasivo(empresa);
	}

	const procesarVacantesCarga = async (data:any) => {
		if (!selectedFile) {
			mostrarDialogo("Advertencia", "No hay un archivo seleccionado para procesar.");
			return;
		}

	try {
	const formData = new FormData();
	formData.append('archivo', selectedFile);

	// Petición al backend
	await postCargueMasivoVacantes(formData);

	// ÉXITO (HTTP 200)
	mostrarDialogo(
		"Proceso Exitoso", 
		"El cargue masivo de vacantes se ha procesado correctamente."
	);
	
	// Limpieza del estado del formulario
	setUploadedData(null);
	setSelectedFile(null);
	setKeySelect(null);
	setVacantes(null);

	} catch (error: any) {
	console.error('Error completo en la petición:', error);

	const status = error?.response?.status;
	const responseData = error?.response?.data;

	// =========================================================================
	// CASO 1: STATUS 422 - ERRORES DE VALIDACIÓN EN EL CONTENIDO DEL ARCHIVO
	// =========================================================================
	if (status === 422) {
		// 1. Extraer el resumen de las cabeceras (Búsqueda insensible a mayúsculas/minúsculas)
		const headers = error?.response?.headers || {};
		const resumenHeader = 
		headers['x-resumen-procesamiento'] || 
		headers['X-Resumen-Procesamiento'] ||
		headers['X-RESUMEN-PROCESAMIENTO'];

		let resumen = { exitosos: 0, fallidos: 0, total: 0 };

		if (resumenHeader) {
		try {
			resumen = typeof resumenHeader === 'string' ? JSON.parse(resumenHeader) : resumenHeader;
		} catch (e) {
			console.error("Error parseando x-resumen-procesamiento:", e);
		}
		}

		// 2. Descargar CSV agregando el BOM UTF-8 (\uFEFF) para evitar caracteres corruptos (NÃºmero, PELÃCULAS)
		const csvData = typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
		
		// El prefijo '\uFEFF' obliga a Excel/Windows a interpretar los caracteres en UTF-8 correcto
		const blob = new Blob(['\uFEFF' + csvData], { type: 'text/csv;charset=utf-8;' });
		
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', `errores_carga_vacantes_${Date.now()}.csv`);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);

		// 3. Mensaje ajustado al usuario
		const totalFallidos = resumen.fallidos || 1;
		const totalProcesados = resumen.total || 1;

		const mensajeValidacion = 
		`El archivo no se procesó debido a errores en la información ingresada.\n\n` +
		`• Registros evaluados: ${totalProcesados}\n` +
		`• Exitosos: ${resumen.exitosos || 0}\n` +
		`• Registros con inconsistencias: ${totalFallidos}\n\n` +
		`Se ha descargado automáticamente un archivo con el detalle de los errores. Consulta la última columna ('ERRORES_DETALLADOS') para corregir el archivo e intentarlo nuevamente.`;

		mostrarDialogo("Validación de Contenido Fallida", mensajeValidacion);

		setSelectedFile(null);	

	} else {
		let mensajeApi = 'No se pudo establecer conexión con el servidor.';

		if (typeof responseData === 'string') {
		try {
			const parsed = JSON.parse(responseData);
			mensajeApi = parsed.mensaje || parsed.message || responseData;
		} catch (e) {
			mensajeApi = responseData || error?.message || mensajeApi;
		}
		} else if (responseData) {
		mensajeApi = responseData.mensaje || responseData.message || error?.message || mensajeApi;
		} else if (error?.message) {
		mensajeApi = error.message;
		}

		mostrarDialogo("Error en Carga Masiva", `No se pudieron registrar las vacantes:\n${mensajeApi}`);
	}
	}
	
	};


	const descargarPlantillaVacantes = () => {
		const link = document.createElement("a");
		link.href = "/plantillas/Plantilla_Vacantes.xlsx";
		link.download = "Plantilla_Vacantes.xlsx";
		document.body.appendChild(link);
		link.click();
		link.remove();
	};

	const descargarPlantillaEmpresas = () => {
		const link = document.createElement("a");
		link.href = "/plantillas/Plantilla_Empresas.xlsx";
		link.download = "Plantilla_Empresas.xlsx";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};


	return (
		<div className='mt-5'>
			<div className='mb-4'>
				<BackButton />
			</div>
			{/* Paso 1: Descarga de plantillas */}
			<div
				className='p-5 mb-8'
				style={{ backgroundColor: '#F3F8FF', border: '1px solid #99aec9ff', borderRadius: '12px' }}
			>
				<h3 className='fw-bold mb-6' style={{ color: '#1B2559' }}>
					PASO 1: Descarga la plantilla oficial (Si aún no la tienes)
				</h3>
				<div className='row g-5'>
					{templateCards.map((card, i) => (
						<div className='col-12 col-md-4' key={i}>
							<div
								className='h-100 p-5 bg-white d-flex flex-column'
								style={{ borderRadius: '10px', boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}
							>
								<div
									className='d-flex align-items-center justify-content-center mb-4'
									style={{ width: '48px', height: '48px', borderRadius: '10px', backgroundColor: '#E8F0FE' }}
								>
									<i className={`bi ${card.icon}`} style={{ fontSize: '22px', color: '#2266CC' }}></i>
								</div>
								<h5 className='fw-bold mb-2' style={{ color: '#1B2559' }}>{card.title}</h5>
								<p className='text-muted mb-5'>{card.subtitle}</p>
								<button
									type='button'
									className='btn mt-auto'
									style={{ border: '1px solid #2266CC', color: '#2266CC', backgroundColor: '#FFFFFF' }}
									onClick={() => handleDescargarPlantilla(card.key)}
									disabled={card.key === 'candidatos' && cargueCandidatos.cargandoParametros}
								>
									<i className='bi bi-download me-2'></i>
									{card.key === 'candidatos' && cargueCandidatos.cargandoParametros
										? 'Cargando catálogos...'
										: 'Descargar Plantilla (.xlsx)'}
								</button>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Paso 2: Carga de datos */}
			<div
				className='p-5'
				style={{ backgroundColor: '#F3F8FF', border: '1px solid #D6E4F5', borderRadius: '12px' }}
			>
				<h3 className='fw-bold mb-6' style={{ color: '#1B2559' }}>
				  PASO 2: Selecciona la entidad y sube tus datos
				</h3>
				<div className='row g-5'>
					{uploadCards.map((card, i) => (
						<div className='col-12 col-md-4' key={i}>
							<div
								className='h-100 p-5 bg-white d-flex flex-column'
								style={{ borderRadius: '10px', boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}
							>
								<div
									className='d-flex align-items-center justify-content-center mb-4'
									style={{ width: '48px', height: '48px', borderRadius: '10px', backgroundColor: '#2266CC' }}
								>
									<i className={`bi ${card.icon}`} style={{ fontSize: '22px', color: '#FFFFFF' }}></i>
								</div>
								<h5 className='fw-bold mb-2' style={{ color: '#1B2559' }}>{card.title}</h5>
								<p className='text-muted mb-5'>{card.description}</p>
								<button
									type='button'
									className='btn btn-primary mt-auto'
									style={{ backgroundColor: '#2266CC', borderColor: '#2266CC' }}
									onClick={() => handleGestionarCarga(card.key)}
									disabled={procesandoArchivo}
								>
									{procesandoArchivo && selectedUpload === card.key ? (
										<>
											<span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
											Leyendo archivo...
										</>
									) : (
										'Gestionar Carga de Entidad'
									)}
								</button>
							</div>
						</div>
					))}
				</div>
				<div
					className='mt-5 p-4'
					style={{ backgroundColor: '#FFFFFF', border: '1px solid #D6E4F5', borderRadius: '10px', color: '#1B2559' }}
				>
					<p className='fw-bold mb-2'>
						<i className='bi bi-info-circle me-2' style={{ color: '#2266CC' }}></i>
						Formatos aceptados: .xlsx, .xls y .csv 
					</p>
					<p className='mb-2'>Si carga un archivo .csv, debe estar codificado en <strong>UTF-8</strong>:</p>
					<ul className='mb-0'>
						<li>
							En Excel, use <strong>Archivo → Guardar como → CSV UTF-8 (delimitado por comas) (*.csv)</strong> con la hoja "Plantilla" activa.
						</li>
					</ul>
				</div>
				<input
					type='file'
					accept='.xlsx, .xls, .csv'
					ref={fileInputRef}
					style={{ display: 'none' }}
					onChange={(e) => handleFileUpload(e, selectedUpload)}
				/>
			</div>

			{/* Renderizado temporal de los datos JSON */}
			{uploadedData && (
				<div
					className='p-5 mt-8'
					style={{ backgroundColor: '#F9FAFB', border: '1px solid #D6E4F5', borderRadius: '12px' }}
				>
					<h3 className='fw-bold mb-4' style={{ color: '#1B2559' }}>
						Previsualización de Datos Cargados ({keySelect})
					</h3>
					<div
						className='bg-white p-4'
						style={{ borderRadius: '8px', maxHeight: '400px', overflowY: 'auto', border: '1px solid #E5E7EB' }}
					>
						{uploadedData.length > 0 ? (
							<table className='table table-striped table-hover align-middle gs-0 gy-4'>
								<thead>
									<tr className='fw-bold text-muted bg-light'>
										{Object.keys(uploadedData[0]).map((key) => (
											<th key={key} className='ps-4 min-w-125px rounded-start rounded-end'>{key}</th>
										))}
									</tr>
								</thead>
								<tbody>
									{uploadedData.map((row, rowIndex) => (
										<tr key={rowIndex}>
											{Object.values(row).map((val: any, colIndex) => (
												<td key={colIndex} className='ps-4' dangerouslySetInnerHTML={{ __html: String(val ?? '') }} />
											))}
										</tr>
									))}
								</tbody>
							</table>
						) : (
							<p className='text-muted mb-0 text-center py-4'>No hay datos válidos para mostrar.</p>
						)}
					</div>
					<div className='mt-4 d-flex justify-content-end'>
						{uploadedData.some((row: any) =>
							['ERROR', 'EXISTE', 'CREADA'].some((estado) =>
								String(row['Estado'] ?? '').toUpperCase().includes(estado)
							)
						) && (
							<button
								type='button'
								className='btn btn-outline-danger me-3'
								onClick={() =>
									keySelect === 'empresas'
										? descargarErroresCsv(tieneError ? "Errores" : "Resumen")
										: descargarErroresTxt(tieneError ? "Errores" : "Resumen")
								}
							>
								<i className='bi bi-file-earmark-text me-2'></i>
								{tieneError
									? `Descargar errores (${keySelect === 'empresas' ? '.csv' : '.txt'})`
									: `Descargar resumen (${keySelect === 'empresas' ? '.csv' : '.txt'})`}
							</button>
						)}
						<button
							type='button'
							className='btn btn-light me-3'
							onClick={() => {
								setUploadedData(null);
								setKeySelect(null);
								setVacantes(null);
								cargueCandidatos.reset();
								setHayErroresCandidatos(false);
							}}
						>
							Limpiar Datos
						</button>
						<button
							type='button'
							className='btn btn-primary'
							style={{ backgroundColor: '#2266CC', borderColor: '#2266CC' }}
							onClick={handleProcesarCarga}
							disabled={procesandoCarga}
						>
							{procesandoCarga ? (
								<>
									<span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
									Procesando...
								</>
							) : (
								'Procesar Carga'
							)}
						</button>
					</div>
				</div>
			)}

			{/* Dialogo */}			
			<Dialog open={openModal} onClose={handleCloseModal}>
				<DialogTitle>{tituloModal}</DialogTitle>
				<DialogTitle
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1
					}}
				>
					{obtenerIconoDialogo()}
					{tituloModal}
				</DialogTitle>
				<DialogContent>
					<DialogContentText component='div' sx={{ whiteSpace: 'pre-line', overflow: 'visible', textOverflow: 'unset', wordBreak: 'break-word' }}
				>
					{mensajeError}
				</DialogContentText>
				</DialogContent>
				<DialogActions>	
					{botonAceptarModal && (
						<button  type='button' className='btn btn-primary mt-auto' style={{ backgroundColor: '#2266cc', borderColor: '#2266CC' }} onClick={handleAceptarConfirmar}>
							Aceptar
						</button>
					)}
					<button type='button' className='btn btn-primary mt-auto' style={{ backgroundColor: '#4a4d52ff', borderColor: '#2266CC' }} onClick={handleCloseModal}>
						Cerrar
					</button>
				</DialogActions>
			</Dialog>

			{/* Loading en la mitad de la pantalla */}		
			<Box mt={5}>
				<LoadingBackdrop loading={loading} />
			</Box>			
		</div>
	);
};

const CarguesMasivosWrapper = () => {
	return (
		<>
			<PageTitle breadcrumbs={[]}>Centro de Cargues Masivos</PageTitle>
			<CarguesMasivosPage />
		</>
	);
};

export { CarguesMasivosWrapper };
